<template>
  <div class="camera-wrapper">
    <div class="camera-view">
      <video
        ref="videoEl"
        autoplay
        playsinline
        class="video-feed"
      ></video>

      <div class="actions">
        <ButtonDefault color="blue" :disabled="running" @click="startCamera">
          Iniciar
        </ButtonDefault>

        <ButtonDefault color="red" :disabled="!running" @click="stopCamera">
          Detener
        </ButtonDefault>

        <ButtonDefault color="green" :disabled="!running" @click="takePhoto">
          Guardar
        </ButtonDefault>
      </div>
    </div>

    <!-- canvas oculto para capturar -->
    <canvas ref="canvasEl" style="display:none;"></canvas>
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeUnmount } from 'vue'

/**
 * Props
 * Recibes una función setFoto que acepta el base64/dataURL
 */
const { setFoto } = defineProps<{
  setFoto: (foto: string) => void
}>()

/**
 * Refs a elementos DOM
 * HTMLVideoElement y HTMLCanvasElement son tipos nativos del DOM
 */
const videoEl = ref<HTMLVideoElement | null>(null)
const canvasEl = ref<HTMLCanvasElement | null>(null)

/**
 * Estado interno
 */
const running = ref<boolean>(false)
const streamRef = ref<MediaStream | null>(null)

/**
 * Inicia cámara
 */
async function startCamera(): Promise<void> {
  // getUserMedia solo se expone en contextos seguros (HTTPS o localhost). Servido
  // por HTTP plano el navegador no lo entrega: se avisa con un mensaje claro en
  // vez de reventar con "Cannot read properties of undefined".
  if (!navigator.mediaDevices?.getUserMedia) {
    const aviso = 'La cámara requiere una conexión segura (HTTPS). Contacte a soporte.'
    console.warn('[camera]', aviso)
    window.notificacion.notificacion('warning', aviso)
    return
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'environment' },
      audio: false
    })

    streamRef.value = stream
    running.value = true

    if (videoEl.value) {
      videoEl.value.srcObject = stream
      // .play() devuelve Promise<void>
      await videoEl.value.play()
    }
  } catch (err: unknown) {
    console.error('No se pudo acceder a la cámara:', err)

    // err es unknown. Lo forzamos seguro:
    const message =
      err instanceof Error
        ? err.message
        : typeof err === 'string'
          ? err
          : 'Error desconocido'

    alert('Error al acceder a la cámara: ' + message)
  }
}

/**
 * Detiene cámara
 */
function stopCamera(): void {
  if (streamRef.value) {
    // liberamos tracks
    streamRef.value.getTracks().forEach(t => t.stop())
  }

  streamRef.value = null
  running.value = false

  if (videoEl.value) {
    videoEl.value.srcObject = null
  }
}

/**
 * Toma foto y entrega dataURL a setFoto
 */
function takePhoto(): void {
  if (!videoEl.value || !canvasEl.value) return

  const video = videoEl.value
  const w = video.videoWidth
  const h = video.videoHeight

  if (!w || !h) {
    alert('La cámara aún no devolvió dimensiones válidas')
    return
  }

  const canvas = canvasEl.value
  canvas.width = w
  canvas.height = h

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    console.error('No se pudo obtener el contexto 2D del canvas')
    return
  }

  ctx.drawImage(video, 0, 0, w, h)

  // genera dataURL en JPEG
  const dataUrl = canvas.toDataURL('image/jpeg', 0.9)

  setFoto(dataUrl)
}

/**
 * Limpieza al desmontar
 */
onBeforeUnmount(() => {
  stopCamera()
})
</script>

<style scoped>
.camera-wrapper {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
  max-width: 400px;
}

.camera-view {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.video-feed {
  width: 250px;
  height: 100px;
  max-width: 90vw;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #000;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
</style>
